<template>
  <div :class="rootClass">
    <div class="flex items-start justify-between gap-2">
      <OfferPrice
        :offer="offerForPrice"
        :offer-price="offer.price.amount"
        :source="source"
        :cashback-click-source="cashbackClickSource"
        @onCashbackInfoClick="emit('onCashbackClick')"
      />
      <div
        v-if="allCountryCodes.length > 0"
        class="flex shrink-0 -space-x-2"
      >
        <img
          v-for="(code, index) in allCountryCodes"
          :key="code"
          :src="staticUrls.countryFlagUrl(code)"
          :alt="code"
          :style="`z-index: ${allCountryCodes.length - index}`"
          class="border-background-neutral-secondary h-6 w-6 rounded-full border-2 object-cover"
        />
      </div>
    </div>
    <div v-if="offer.mappedFlights.length > 0" class="mt-3 space-y-3">
      <RecommendedOfferFlight
        v-for="(mappedFlight, flightIndex) in offer.mappedFlights"
        :key="flightIndex"
        :flight-idx="flightIndex"
        :flight="mappedFlight"
        :source="source"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { flightEntity } from 'freedom-shared';

  import { OfferPrice } from '@/widgets/search-result-card';

  import type { MappedRecommendedOffer } from '@/features/favorites';

  import { staticUrls } from '@/shared/config';
  import type { analyticsEventsLib } from '@/shared/lib';

  import RecommendedOfferFlight from './RecommendedOfferFlight.vue';

  const props = withDefaults(
    defineProps<{
      offer: MappedRecommendedOffer;
      source: string;
      cashbackClickSource: analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource;
      /**
       * When `true`, drops the card's own background / border / rounding so
       * it can sit inside a unified container that owns those styles.
       */
      flat?: boolean;
    }>(),
    { flat: false },
  );

  const emit = defineEmits<{ onCashbackClick: [] }>();

  const rootClass = computed(() =>
    props.flat
      ? 'w-full px-4 py-3'
      : 'bg-background-neutral-secondary border-situational-lightgrey w-full rounded-3xl border px-4 py-3',
  );

  /**
   * `OfferPrice` was authored against `flightEntity.types.MappedOffer` (the
   * rich Airflow shape used on SERP), but the recommended-offers card has
   * always fed it the simpler hot-offers shape. The two are runtime-compatible
   * for everything `OfferPrice` reads (price, source, cashback), so we cast
   * at this boundary to silence the structural mismatch without leaking the
   * `MappedRecommendedOffer` shape into the shared widget.
   */
  const offerForPrice = computed(
    () => props.offer as unknown as flightEntity.types.MappedOffer,
  );

  /**
   * Flat, deduplicated list of every country the offer touches across all
   * its flights, preserving route order (origin → layovers → destination,
   * outbound first, then return). Rendered as an overlapping flag strip in
   * the header — same stacking pattern as `AirlineIconsGroup` (horizontal
   * mode): negative `space-x` + descending `z-index`.
   */
  const allCountryCodes = computed(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const flightCodes of props.offer.flightCountryCodes) {
      for (const code of flightCodes) {
        if (!seen.has(code)) {
          seen.add(code);
          ordered.push(code);
        }
      }
    }
    return ordered.slice(1);
  });
</script>
